import { composeWrappers } from "@redotech/react-util/component";
import { OutboundViewsProvider } from "@redotech/redo-merchant-fulfillment-app/outbound-views";
import { AssignedFulfillmentServicesService } from "@redotech/redo-merchant-fulfillment-app/services/assigned-fulfillment-service";
import { LocationService } from "@redotech/redo-merchant-fulfillment-app/services/location-service";
import { ParcelService } from "@redotech/redo-merchant-fulfillment-app/services/parcel-service";
import { ConsolidationViewsProvider } from "@redotech/redo-merchant-fulfillment-app/wms/consolidation/consolidation-views";
import { WorkOrderViewsProvider } from "@redotech/redo-merchant-fulfillment-app/work-orders/work-order-views";
import { InventoryViewsProvider } from "@redotech/redo-merchant-inventory-app/inventory-views";
import { OrderTrackingOrderViewsProvider } from "@redotech/redo-merchant-marketing-app/order-tracking/orders/order-tracking-order-views";
import { OrderTrackingShipmentViewsProvider } from "@redotech/redo-merchant-marketing-app/order-tracking/shipments/order-tracking-shipment-views";
import { ConversationTagsService } from "@redotech/redo-merchant-support-app/services/conversation-tags-service";
import { EmailIntegrationsService } from "@redotech/redo-merchant-support-app/services/email-integrations";
import { MacrosService } from "@redotech/redo-merchant-support-app/services/macros-service";
import { PhoneNumberService } from "@redotech/redo-merchant-support-app/services/phone-number-service";
import { SupportViewOrderingService } from "@redotech/redo-merchant-support-app/services/support-view-ordering-service";
import { VoiceDeviceStateContextProvider } from "@redotech/redo-merchant-support-app/services/voice-device-state";
import { SupportViewsFoldersProvider } from "@redotech/redo-merchant-support-app/v2/providers/support-views-folders-provider";
import { ViewsProvider } from "@redotech/redo-merchant-support-app/views";
import { memo, ReactElement } from "react";
import { FeedbackPopoverProvider } from "../app/navigation/top-navigation/feedback-popover-context";
import { RivoIntegrationProvider } from "../app/rivo-integration-provider";
import { ConciergeOnboardingAnalyticsProvider } from "../concierge/context/concierge-onboarding-analytics-provider";
import { FeatureOnboarding } from "../feature-onboarding";
import { SettingsMovingModalGate } from "../feature-onboarding/settings-moving-modal-gate";
import { WithdrawalsOnboardingTriggerProvider } from "../feature-onboarding/withdrawals-onboarding-trigger";
import { ProductSuiteProvider } from "../redo-suite/product-suite";
import { ReturnsNavRearrangementPopup } from "../return/returns-nav-rearrangement-popup";
import { ClaimViewsProvider } from "./claims/claim-views";
import { ClaimViewOrderingService } from "./claims/claims-view-ordering-service";
import { TeamSwitcherService } from "./global/team-switcher-service";
import { UserProfileService } from "./global/user-profile-service";
import { DiscountsService } from "./marketing/discounts-service";
import { ReviewsToReviewService } from "./marketing/reviews-to-review-service";
import { SegmentsService } from "./marketing/segments-service";
import { OrdersReviewCountService } from "./orders/orders-review-count-service";
import { RecycleViewOrderingService } from "./recycle/recycle-view-ordering-service";
import { RecycleViewsProvider } from "./recycle/recycle-views";
import { ReturnViewsProvider } from "./returns/return-views";
import { ReturnViewOrderingService } from "./returns/returns-view-ordering-service";
import { WarrantyViewOrderingService } from "./warranties/warranty-view-ordering-service";
import { WarrantyViewsProvider } from "./warranties/warranty-views";

export const DataServices = memo(function DataServices({
  children,
}: {
  children: ReactElement;
}) {
  return composeWrappers(
    (inner) => (
      <SupportViewsFoldersProvider>{inner}</SupportViewsFoldersProvider>
    ),
    (inner) => <RivoIntegrationProvider>{inner}</RivoIntegrationProvider>,
    (inner) => <ReturnViewsProvider>{inner}</ReturnViewsProvider>,
    (inner) => <ClaimViewsProvider>{inner}</ClaimViewsProvider>,
    (inner) => <WarrantyViewsProvider>{inner}</WarrantyViewsProvider>,
    (inner) => <RecycleViewsProvider>{inner}</RecycleViewsProvider>,
    (inner) => (
      <ConciergeOnboardingAnalyticsProvider>
        {inner}
      </ConciergeOnboardingAnalyticsProvider>
    ),
    (inner) => <OutboundViewsProvider>{inner}</OutboundViewsProvider>,
    (inner) => <WorkOrderViewsProvider>{inner}</WorkOrderViewsProvider>,
    (inner) => <ConsolidationViewsProvider>{inner}</ConsolidationViewsProvider>,
    (inner) => <InventoryViewsProvider>{inner}</InventoryViewsProvider>,
    (inner) => (
      <OrderTrackingShipmentViewsProvider>
        {inner}
      </OrderTrackingShipmentViewsProvider>
    ),
    (inner) => (
      <OrderTrackingOrderViewsProvider>{inner}</OrderTrackingOrderViewsProvider>
    ),
    (inner) => <PhoneNumberService>{inner}</PhoneNumberService>,
    (inner) => <ConversationTagsService>{inner}</ConversationTagsService>,
    (inner) => <MacrosService>{inner}</MacrosService>,
    (inner) => (
      <VoiceDeviceStateContextProvider>{inner}</VoiceDeviceStateContextProvider>
    ),
    (inner) => <EmailIntegrationsService>{inner}</EmailIntegrationsService>,
    (inner) => <ParcelService>{inner}</ParcelService>,
    (inner) => <LocationService>{inner}</LocationService>,
    (inner) => (
      <AssignedFulfillmentServicesService>
        {inner}
      </AssignedFulfillmentServicesService>
    ),
    (inner) => <ProductSuiteProvider>{inner}</ProductSuiteProvider>,
    (inner) => <SegmentsService>{inner}</SegmentsService>,
    (inner) => <DiscountsService>{inner}</DiscountsService>,
    (inner) => <ViewsProvider>{inner}</ViewsProvider>,
    (inner) => <SupportViewOrderingService>{inner}</SupportViewOrderingService>,
    (inner) => <ReturnViewOrderingService>{inner}</ReturnViewOrderingService>,
    (inner) => <ClaimViewOrderingService>{inner}</ClaimViewOrderingService>,
    (inner) => (
      <WarrantyViewOrderingService>{inner}</WarrantyViewOrderingService>
    ),
    (inner) => <RecycleViewOrderingService>{inner}</RecycleViewOrderingService>,
    (inner) => <OrdersReviewCountService>{inner}</OrdersReviewCountService>,
    (inner) => (
      <WithdrawalsOnboardingTriggerProvider>
        {inner}
      </WithdrawalsOnboardingTriggerProvider>
    ),
    // Above UserProfileService so its "Give feedback" menu item can open it.
    (inner) => <FeedbackPopoverProvider>{inner}</FeedbackPopoverProvider>,
    (inner) => <UserProfileService>{inner}</UserProfileService>,
    (inner) => <TeamSwitcherService>{inner}</TeamSwitcherService>,
    (inner) => <ReviewsToReviewService>{inner}</ReviewsToReviewService>,
    (inner) => (
      <>
        <FeatureOnboarding />
        <SettingsMovingModalGate />
        <ReturnsNavRearrangementPopup />
        {inner}
      </>
    ),
  )(children);
});
